// React
import { useMemo, useRef } from "react";

// MUI
import { Stack, Typography } from "@mui/material";

// Redux
import { useDispatch } from "react-redux";
import { openSnackbar } from "Store/SnackbarSlice";

// Libraries
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

// Components
import { MBSChip } from "../MBSChip";

// Style
import { styles } from "./styles";

const formatDob = (dob) => {
  if (!dob) {
    return "";
  }

  const parsedDob = DateTime.fromISO(String(dob));
  return parsedDob.isValid ? parsedDob.toFormat("LLL d, yyyy") : "";
};

const getDisplayValue = (value, fallback) => value || fallback;

export const MBSPatientInfoHeader = ({ patient, paper = false }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const fallbackValue = t("patientForm.notProvided");

  const InfoCell = ({ value, fullWidth = false }) => (
    <Stack sx={styles.infoCell(fullWidth)}>
      <Typography variant="caption" color="text.secondary">
        {t(`common.patientInfo.${value}`)}
      </Typography>
      <Typography variant="body1" sx={styles.valueTypography}>
        {displayValues[value]}
      </Typography>
    </Stack>
  );

  const displayValues = useMemo(
    () => ({
      name: getDisplayValue(
        `${patient?.firstName || ""} ${patient?.lastName || ""}`.trim(),
        fallbackValue
      ),
      healthCardNumber: getDisplayValue(
        patient?.healthCardNumber,
        fallbackValue
      ),
      dob: getDisplayValue(formatDob(patient?.dob), fallbackValue),
    }),
    [fallbackValue, patient]
  );

  const errorDispatched = useRef(false);

  if (!patient && !errorDispatched.current) {
    errorDispatched.current = true;
    dispatch(
      openSnackbar({
        severity: "error",
        message: t("errors.errorDisplayingPatientInfo"),
      })
    );
  }

  return (
    <Stack
      direction={{ xs: "column-reverse", sm: "row" }}
      sx={styles.container(paper)}
    >
      <Stack direction="row" sx={styles.infoGroup}>
        <InfoCell value="name" fullWidth />
        <InfoCell value="healthCardNumber" />
        <InfoCell value="dob" />
      </Stack>
      <MBSChip inSystem={patient?.inSystem} sx={{ py: 0.75, px: 1.25 }} />
    </Stack>
  );
};
